/** Snap float noise before display (e.g. 0.32000000000000006 → 0.32). */
export function normalizeFloat(value: number, maxFractionDigits: number): number {
  const factor = 10 ** maxFractionDigits;
  return Math.round(value * factor) / factor;
}

export function formatDisplayNumber(
  value: number,
  options?: { maxFractionDigits?: number; minFractionDigits?: number }
): string {
  if (!Number.isFinite(value)) return '—';

  const maxFractionDigits = options?.maxFractionDigits ?? 4;
  const normalized = normalizeFloat(value, maxFractionDigits);

  if (Number.isInteger(normalized)) {
    return normalized.toLocaleString();
  }

  return normalized.toLocaleString(undefined, {
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: options?.minFractionDigits ?? 0,
  });
}

export function isWeightFieldKey(fieldKey: string): boolean {
  const key = fieldKey.trim().toLowerCase();
  return key === 'reaction_weight' || key.endsWith('_weight');
}

export function formatReactionWeightValue(value: unknown): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return formatReactionWeightValue(parsed);
    return trimmed;
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  if (value >= 0 && value <= 1) {
    return formatDisplayNumber(value, { maxFractionDigits: 2 });
  }
  return formatDisplayNumber(value, { maxFractionDigits: 4 });
}

/** Display formatter for dataset viewer cells (weights, counts, nested JSON). */
export function formatDatasetFieldValue(value: unknown, fieldKey?: string): string {
  if (typeof value === 'string') {
    if (fieldKey && isWeightFieldKey(fieldKey)) {
      return formatReactionWeightValue(value);
    }
    return value;
  }
  if (typeof value === 'number') {
    if (fieldKey && isWeightFieldKey(fieldKey)) {
      return formatReactionWeightValue(value);
    }
    return formatDisplayNumber(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === null || value === undefined) return '—';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
